import React from "react";
import {
  Box,
  Button,
  MenuItem,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import {
  FieldContainer,
  FieldLabel,
  InputWrapper,
  StyledTextField,
  StyledTextarea,
  StyledSelect,
  ErrorText,
} from "../StyleField/StyleFields";

import { GradientButton } from "./RegisterTalentForm";
import { useGetCategories } from "../../services/api/card.api";
import { useErrorResponse } from "../../hooks/useErrorResponse";

interface StepOneProps {
  touched: any;
  errors: any;
  values: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  handleNext: () => void;
}

export const StepOne: React.FC<StepOneProps> = ({
  touched,
  errors,
  values,
  handleChange,
  setFieldValue,
  handleNext,
}) => {

  const { data: categoryData, error: categoryError } = useGetCategories({
    key: ["categories"],
  });
  useErrorResponse(categoryError);

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <FieldLabel
          style={{ fontSize: "20px", fontWeight: 700, marginBottom: 0 }}
        >
          Tell us more about your talent profile
        </FieldLabel>
      </Box>

      {/* Talent Name */}
      <FieldContainer>
        <FieldLabel>Talent Name</FieldLabel>
        <StyledTextField
          name="username"
          placeholder="Talent Name"
          value={values.username}
          onChange={handleChange}
          error={Boolean(errors.username) && touched.username}
        />
        {errors.username && touched.username && <ErrorText>{errors.username}</ErrorText>}
      </FieldContainer>

      {/* Category */}
      <FieldContainer>
        <FieldLabel>Category</FieldLabel>
        <InputWrapper>
          <StyledSelect
            name="categories"
            value={values.categories}
            onChange={(event: SelectChangeEvent<unknown>) => {
              setFieldValue("categories", event.target.value);
            }}
            displayEmpty
            inputProps={{ "aria-label": "Select Category" }}
          >
            <MenuItem value="">
              <em>Select Category</em>
            </MenuItem>
            {categoryData?.data?.map((category) => (
              <MenuItem key={category?.slug} value={category?.name}>
                {category?.name}
              </MenuItem>
            ))}
          </StyledSelect>
        </InputWrapper>
        {errors.categories && touched.categories && <ErrorText>{errors.categories}</ErrorText>}
      </FieldContainer>

      {/* Bio */}
      <FieldContainer>
        <FieldLabel>Enter your Bio</FieldLabel>
        <InputWrapper>
          <StyledTextarea
            name="bio"
            multiline
            rows={4}
            placeholder="Some interesting facts about you..."
            value={values.bio}
            onChange={handleChange}
            error={Boolean(errors.bio) && touched.bio}
          />
        </InputWrapper>
        {errors.bio && touched.bio && <ErrorText>{errors.bio}</ErrorText>}
        <Box
          sx={{ textAlign: "right", mt: 0.5, color: "#999", fontSize: "12px" }}
        >
          {values.bio?.length || 0}/1000
        </Box>
      </FieldContainer>

      {/* Portfolio Links */}
      <FieldContainer>
        <FieldLabel>
          Portfolio Links{" "}
          <span
            style={{ fontStyle: "italic", color: "#6B7280", fontWeight: 400 }}
          >
            (Optional)
          </span>
        </FieldLabel>

        {values.portfolio_links.map((link: string, index: number) => (
          <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
            <StyledTextField
              name={`portfolio_links[${index}]`}
              placeholder="http://example.com"
              value={link}
              onChange={handleChange}
              fullWidth
              error={Boolean(errors.portfolio_links?.[index]) && touched.portfolio_links?.[index]}
              
            />
            <IconButton
              type="button"
              onClick={() => {
                const updatedLinks = [...values.portfolio_links];
                updatedLinks.splice(index, 1);
                setFieldValue("portfolio_links", updatedLinks);
              }}
              aria-label="Remove"
              size="small"
              sx={{ mt: "6px" }}
            >
              ❌
            </IconButton>
          </Box>
        ))}

        <Button
          variant="outlined"
          size="small"
          type="button"
          onClick={() =>
            setFieldValue("portfolio_links", [...values.portfolio_links, ""])
          }
          sx={{
            mt: 1,
            px: 1.5,
            py: 0.6,
            fontSize: "13px",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "#D1D5DB",
            color: "#4B5563",
            "&:hover": {
              borderColor: "#6366F1",
              backgroundColor: "#F3F4F6",
              color: "#3730A3",
            },
            width: "fit-content",
            alignSelf: "flex-start",
          }}
        >
          ➕ Add Link
        </Button>
        {typeof errors.portfolio_links === "string" && touched.portfolio_links && (
          <ErrorText>{errors.portfolio_links}</ErrorText>
        )}
      </FieldContainer>

      {/* Social Links */}
      <FieldContainer>
        <FieldLabel>
          Social Links (LinkedIn, Instagram, etc.){" "}
          <span
            style={{ fontStyle: "italic", color: "#6B7280", fontWeight: 400 }}
          >
            (Optional)
          </span>
        </FieldLabel>
        {values.social_links?.map((link: string, index: number) => (
          <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
            <StyledTextField
              name={`social_links[${index}]`}
              placeholder="https://linkedin.com/in/yourname"
              value={link}
              onChange={handleChange}
              fullWidth
              error={Boolean(errors.social_links?.[index]) && touched.social_links?.[index]}
            />
            <IconButton
              type="button"
              onClick={() => {
                const updatedLinks = [...values.social_links];
                updatedLinks.splice(index, 1);
                setFieldValue("social_links", updatedLinks);
              }}
              aria-label="Remove"
              size="small"
              sx={{ mt: "6px" }}
            >
              ❌
            </IconButton>
          </Box>
        ))}
        <Button
          variant="outlined"
          size="small"
          type="button"
          onClick={() =>
            setFieldValue("social_links", [...(values.social_links || []), ""])
          }
          sx={{
            mt: 1,
            px: 1.5,
            py: 0.6,
            fontSize: "13px",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "#D1D5DB",
            color: "#4B5563",
            "&:hover": {
              borderColor: "#6366F1",
              backgroundColor: "#F3F4F6",
              color: "#3730A3",
            },
            width: "fit-content",
            alignSelf: "flex-start",
          }}
        >
          ➕ Add Social
        </Button>
        {typeof errors.social_links === "string" && touched.social_links && (
          <ErrorText>{errors.social_links}</ErrorText>
        )}
      </FieldContainer>

      {/* Navigation */}
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between",
          gap: 2,
          mt: 3 
        }}
      >
        <Button 
          variant="outlined" 
          disabled 
          sx={{ 
            width: { xs: '100%', sm: 180 },
            height: "44px",
            font: { xs: "var(--semibold-sm)", sm: "var(--semibold-md)" },
            color: "var(--neutral-900)",
            borderRadius: "var(--spacing-3x)",
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          Back
        </Button>
        <GradientButton 
          onClick={handleNext} 
          sx={{ 
            width: { xs: '100%', sm: 180 },
            height: "44px",
            font: { xs: "var(--semibold-sm)", sm: "var(--semibold-md)" },
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          Next
        </GradientButton>
      </Box>
    </>
  );
};
